import React from 'react'
import classNames from 'classnames/bind'
import { Divider, Icon } from '@adc/ui-core'
import { NavigationMenuItemProps } from './types'
import { getMenuItemLinkName } from './constants'
import { track } from '@adc/analytics'
import smartSaleLogo from '../../../img/smartsale-logo.svg'
import auctionLogo from '../../../img/auction-logo-mobile.svg'
import styles from './styles.scss'
import headerStyles from '../styles.scss'

const cx = classNames.bind(styles)
const hcx = classNames.bind(headerStyles)

const NavigationMenuItem: React.FC<NavigationMenuItemProps> = ({
  item,
  onMenuItemClick,
  bellNotificationsCount,
  indicatorsEnabled = false,
  trackingContext,
}) => {
  if (item.isDivider) {
    return (
      <div className={cx('menu-divider-wrapper')}>
        <Divider className={cx('menu-divider')} />
      </div>
    )
  }

  if (item.isSectionLabel) {
    return (
      <div className={cx('menu-section-label')} data-elm-id={item.elmId}>
        {item.title}
      </div>
    )
  }

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    const linkName = getMenuItemLinkName(item)

    track('navigation-click', { linkName, ...trackingContext })
    if (onMenuItemClick) {
      onMenuItemClick()
    }

    if (item.onClick) {
      item.onClick(e)
    } else if (item.url && item.url !== '#') {
      window.location.href = item.url
    }
  }

  if (item.isFeaturedCard) {
    return (
      <a
        href={item.url}
        onClick={handleClick}
        className={cx('menu-item', 'featured-card')}
        data-elm-id={item.elmId}
        role='option'
      >
        <span className={cx('featured-card-header')}>
          {item.icon && (
            <Icon
              name={item.icon}
              iconStyle={item.iconStyle}
              className={cx('featured-card-icon')}
            />
          )}
          <span className={cx('featured-card-title')}>{item.title}</span>
        </span>
        {item.subtitle && (
          <span className={cx('featured-card-subtitle')}>{item.subtitle}</span>
        )}
        {item.ctaLabel && (
          <span className={cx('featured-card-cta')}>
            {item.ctaLabel}
            <Icon
              name='chevron-right'
              iconStyle='solid'
              className={cx('featured-card-cta-chevron')}
            />
          </span>
        )}
      </a>
    )
  }

  if (item.isSmartSaleLogo) {
    return (
      <a
        href={item.url}
        onClick={handleClick}
        className={cx('menu-item', 'sell-item')}
        data-elm-id={item.elmId}
        role='option'
      >
        <img
          src={smartSaleLogo}
          alt='Smart Sale'
          className={cx('smartsale-logo')}
        />
        <span className={cx('menu-label')}>{item.title}</span>
      </a>
    )
  }

  if (item.isAuctionLogo) {
    return (
      <a
        href={item.url}
        onClick={handleClick}
        className={cx('menu-item')}
        data-elm-id={item.elmId}
        role='option'
      >
        <img
          src={auctionLogo}
          alt='Auction.com'
          className={cx('smartsale-logo')}
        />
        <span className={cx('menu-label')}>{item.title}</span>
      </a>
    )
  }

  return (
    <a
      href={item.url}
      onClick={handleClick}
      className={cx('menu-item', item.className, {
        'highlighted-item': item.isHighlighted,
      })}
      data-elm-id={item.elmId}
      role='option'
    >
      {item.icon && (
        <Icon
          name={item.icon}
          iconStyle={item.iconStyle}
          className={cx('menu-icon', {
            'highlighted-icon': item.isHighlighted,
          })}
        />
      )}
      <span
        className={cx('menu-label', {
          'highlighted-label': item.isHighlighted,
        })}
      >
        {!indicatorsEnabled &&
          item.id === 'notifications' &&
          bellNotificationsCount && (
            <span
              className={hcx('bell-notification')}
              style={{ marginRight: '3px' }}
              data-elm-id='desktop_notification_count'
            >
              {bellNotificationsCount}
            </span>
          )}
        {item.title}
        {indicatorsEnabled &&
          (item.showIndicator ||
            (item.id === 'notifications' && !!bellNotificationsCount)) && (
            <span
              className={cx('menu-indicator-dot')}
              data-elm-id={`${item.elmId}_indicator`}
            />
          )}
      </span>
    </a>
  )
}

export default NavigationMenuItem
